import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Send, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

// Per-booking trip chat between guest, provider (and admin can join).
export default function TripChat({ bookingId, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/trip-chats/${bookingId}/messages`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [bookingId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/trip-chats/${bookingId}/messages`, { text }); setText(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-md h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="trip-chat">
        <div className="flex items-center justify-between p-4 border-b border-[#1D2640]">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="w-4 h-4 text-fuchsia-400 shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-white text-sm truncate">{data?.booking?.title || "Trip chat"}</p>
              {data?.booking && <p className="text-[11px] text-slate-500 truncate">{data.booking.traveler_name} ↔ {data.booking.provider_name}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!data ? <div className="flex justify-center pt-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
            : data.messages.length === 0 ? <p className="text-center text-slate-500 text-sm pt-10">No messages yet. Say hello 👋</p>
            : data.messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${mine ? "bg-gradient-to-br from-cyan-500 to-blue-600 text-white" : m.sender_role === "admin" ? "bg-orange-500/20 text-orange-100 border border-orange-400/30" : "glass text-slate-200"}`}>
                      <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name}{m.sender_role === "admin" ? " · Admin" : ""}</p>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                  </div>
                );
              })}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-[#1D2640] flex items-center gap-2">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message…" className="flex-1 bg-[#0B0E1B] border border-[#1D2640] rounded-full px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          <button data-testid="chat-send" onClick={send} disabled={sending} className="w-10 h-10 rounded-full btn-neon flex items-center justify-center shrink-0 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
